import { Link } from 'react-router-dom'
import {
  useAuditDashboard,
  useCapaDashboard,
  useEquipmentDashboard,
  useIntegrationsDashboard,
  useNotificationsDashboard,
} from '@/hooks/useDashboardData'
import { previousTimeWindow } from '@/lib/dashboardLayouts'
import { deltaOf, previousPeriodLabel } from '@/lib/dashboardWidgetFormat'
import { windowLabel } from '@/lib/reportRange'
import { cn } from '@/lib/utils'
import { useEventTypeCatalog } from '@/hooks/useEventTypes'
import { eventMeta } from '@/types/maintenanceTask'
import type { LabelCount } from '@/api/dashboardApi'
import type { WidgetBodyProps } from '../builtWidgets'
import { DeltaBadge, Sparkline, STATUS_COLORS } from '../charts'
import { CATEGORICAL, PartsView, SeriesView, dailyRows, partsKind, seriesKind, type Part } from '../views'
import { WidgetQueryState, WidgetStat } from '../WidgetCard'

// ─── Equipment, Resources, CAPA, Notifications, Audit & Integrations widgets
//     (z8nrz7eqyt) ────────────────────────────────────────────────────────────

const counts = (rows: LabelCount[], color?: string): Part[] =>
  rows.map((r, i) => ({ label: r.label, value: r.count, color: color ?? CATEGORICAL[i % CATEGORICAL.length] }))

const humanise = (s: string) => s.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase())

const shortDate = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })

function MiniList({ children }: { children: React.ReactNode }) {
  return <ul className='divide-y divide-gray-100'>{children}</ul>
}

function Row({ title, sub, aside, to, asideTone }: { title: string; sub?: string; aside: string; to?: string; asideTone?: string }) {
  const body = (
    <>
      <span className='min-w-0 flex-1'>
        <span className='block truncate text-xs font-medium text-gray-800'>{title}</span>
        {sub && <span className='block truncate text-[11px] text-gray-400'>{sub}</span>}
      </span>
      <span className={cn('shrink-0 text-[11px] font-medium text-gray-500', asideTone)}>{aside}</span>
    </>
  )
  return (
    <li>
      {to ? (
        <Link to={to} className='-mx-1 flex items-center gap-2 rounded px-1 py-1.5 hover:bg-gray-50'>
          {body}
        </Link>
      ) : (
        <div className='flex items-center gap-2 py-1.5'>{body}</div>
      )}
    </li>
  )
}

// ─── Equipment & resources ───────────────────────────────────────────────────

export function MaintenanceDueWidget({ time, view }: WidgetBodyProps) {
  const q = useEquipmentDashboard(time.query)
  const m = q.data?.maintenance
  const parts: Part[] = m
    ? [
        { label: 'Overdue', value: m.overdue, color: STATUS_COLORS.bad },
        { label: 'Due in 7 days', value: m.dueNext7, color: '#f59e0b' },
        { label: 'In progress', value: m.inProgress, color: STATUS_COLORS.accent },
      ]
    : []
  return (
    <WidgetQueryState isLoading={q.isLoading} error={q.error} isEmpty={false} onRetry={() => void q.refetch()}>
      {m &&
        (view === 'number' ? (
          <WidgetStat
            value={m.overdue + m.dueNext7}
            tone={m.overdue > 0 ? 'red' : m.dueNext7 > 0 ? 'amber' : 'blue'}
            href='/equipment'
            caption='Maintenance overdue or due within 7 days'
            footer={`${m.overdue} overdue · ${m.inProgress} in progress`}
          />
        ) : (
          <PartsView view={partsKind(view, 'stack')} parts={parts} totalLabel='tasks' />
        ))}
    </WidgetQueryState>
  )
}

export function UpcomingMaintenanceWidget({ time, view }: WidgetBodyProps) {
  const q = useEquipmentDashboard(time.query)
  // The tenant's name for each task's type, never its code (z8nrz7fb9k).
  const catalog = useEventTypeCatalog()
  const rows = q.data?.maintenance.next ?? []
  return (
    <WidgetQueryState
      isLoading={q.isLoading}
      error={q.error}
      isEmpty={!!q.data && rows.length === 0}
      emptyText='No maintenance is waiting.'
      onRetry={() => void q.refetch()}
    >
      {view === 'table' ? (
        <table className='w-full text-xs'>
          <thead>
            <tr className='border-b border-gray-100 text-left text-[11px] text-gray-400'>
              <th className='py-1 font-medium'>Equipment</th>
              <th className='py-1 font-medium'>Task</th>
              <th className='py-1 text-right font-medium'>Due</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className='border-b border-gray-50 last:border-0'>
                <td className='py-1.5 text-gray-700'>{r.equipmentLabel}</td>
                <td className='py-1.5 text-gray-500'>{eventMeta(r.eventTypeCode, catalog).label}</td>
                <td className={cn('py-1.5 text-right tabular-nums', r.overdue ? 'font-semibold text-red-600' : 'text-gray-700')}>
                  {shortDate(r.dueDate)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <MiniList>
          {rows.map(r => (
            <Row
              key={r.id}
              to='/equipment'
              title={r.equipmentLabel}
              sub={eventMeta(r.eventTypeCode, catalog).label}
              aside={r.overdue ? `Overdue · ${shortDate(r.dueDate)}` : shortDate(r.dueDate)}
              asideTone={r.overdue ? 'text-red-600' : undefined}
            />
          ))}
        </MiniList>
      )}
    </WidgetQueryState>
  )
}

export function MaintenanceDoneWidget({ time, view }: WidgetBodyProps) {
  const q = useEquipmentDashboard(time.query)
  const m = q.data?.maintenance
  return (
    <WidgetQueryState isLoading={q.isLoading} error={q.error} isEmpty={false} onRetry={() => void q.refetch()}>
      {m &&
        (view === 'number' ? (
          <WidgetStat
            value={m.onTimePct === null ? '—' : `${m.onTimePct}%`}
            href='/equipment'
            caption='Maintenance completed on time'
            trend={<Sparkline points={m.completedByDay} label='completed' color={STATUS_COLORS.good} />}
            footer={`${m.completedInPeriod} tasks completed · ${windowLabel(time.window)}`}
          />
        ) : (
          <div className='flex flex-1 flex-col gap-1'>
            <p className='text-xs text-gray-500'>
              <span className='font-semibold text-gray-900'>{m.completedInPeriod}</span> completed
              {m.onTimePct !== null && (
                <>
                  {' · '}
                  <span className='font-semibold text-gray-900'>{m.onTimePct}%</span> on time
                </>
              )}
            </p>
            <SeriesView
              view={seriesKind(view, 'columns')}
              rows={dailyRows(m.completedByDay)}
              series={[{ key: 'count', label: 'Completed', color: STATUS_COLORS.good }]}
            />
          </div>
        ))}
    </WidgetQueryState>
  )
}

export function EquipmentAvailabilityWidget({ time, view }: WidgetBodyProps) {
  const q = useEquipmentDashboard(time.query)
  const d = q.data
  const parts: Part[] = d
    ? [
        { label: 'Operational', value: d.operational, color: STATUS_COLORS.good },
        { label: 'Out of service', value: d.outOfService, color: STATUS_COLORS.bad },
      ]
    : []
  return (
    <WidgetQueryState
      isLoading={q.isLoading}
      error={q.error}
      isEmpty={!!d && d.total === 0}
      emptyText='No equipment registered.'
      onRetry={() => void q.refetch()}
    >
      {d &&
        (view === 'number' ? (
          <WidgetStat
            value={`${d.availabilityPct}%`}
            tone={d.availabilityPct < 80 ? 'amber' : 'blue'}
            href='/equipment'
            caption='Equipment available (operational)'
            trend={
              <div className='h-2 w-full rounded-full bg-red-100' title={`${d.availabilityPct}% operational`}>
                <div className='h-2 rounded-full bg-green-600' style={{ width: `${d.availabilityPct}%` }} />
              </div>
            }
            footer={`${d.operational} of ${d.total} operational · ${d.outOfService} out of service`}
          />
        ) : (
          <PartsView view={partsKind(view, 'stack')} parts={parts} totalLabel='equipment' />
        ))}
    </WidgetQueryState>
  )
}

export function EquipmentByTypeWidget({ time, view }: WidgetBodyProps) {
  const q = useEquipmentDashboard(time.query)
  const parts = counts(q.data?.byType ?? [], view === 'bars' ? '#15803d' : undefined)
  return (
    <WidgetQueryState
      isLoading={q.isLoading}
      error={q.error}
      isEmpty={parts.length === 0}
      emptyText='No equipment registered.'
      onRetry={() => void q.refetch()}
    >
      <PartsView view={partsKind(view)} parts={parts} totalLabel='units' />
    </WidgetQueryState>
  )
}

export function ReservationsTodayWidget({ time, view }: WidgetBodyProps) {
  const q = useEquipmentDashboard(time.query)
  const r = q.data?.reservations
  return (
    <WidgetQueryState isLoading={q.isLoading} error={q.error} isEmpty={false} onRetry={() => void q.refetch()}>
      {r &&
        (view !== 'number' ? (
          <PartsView
            view={partsKind(view, 'donut')}
            totalLabel='operational'
            parts={[
              { label: 'Booked today', value: r.equipmentBookedToday, color: STATUS_COLORS.accent },
              { label: 'Free today', value: r.equipmentFreeToday, color: STATUS_COLORS.accentLighter },
            ]}
          />
        ) : (
          <WidgetStat
            value={r.today}
            href='/equipment'
            caption='Equipment reservations covering today'
            footer={`${r.equipmentBookedToday} units booked · ${r.equipmentFreeToday} operational units free`}
          />
        ))}
    </WidgetQueryState>
  )
}

export function ResourceAvailabilityWidget({ time, view }: WidgetBodyProps) {
  const q = useEquipmentDashboard(time.query)
  const d = q.data
  const parts: Part[] = d
    ? [
        { label: 'Booked today', value: d.reservations.equipmentBookedToday, color: STATUS_COLORS.accent },
        { label: 'Free today', value: d.reservations.equipmentFreeToday, color: STATUS_COLORS.good },
        { label: 'Out of service', value: d.outOfService, color: STATUS_COLORS.muted },
      ]
    : []
  const usable = d ? d.reservations.equipmentBookedToday + d.reservations.equipmentFreeToday : 0
  return (
    <WidgetQueryState
      isLoading={q.isLoading}
      error={q.error}
      isEmpty={!!d && d.total === 0}
      emptyText='No equipment registered.'
      onRetry={() => void q.refetch()}
    >
      {d &&
        (view === 'number' ? (
          <WidgetStat
            value={usable > 0 ? `${Math.round((d.reservations.equipmentBookedToday / usable) * 100)}%` : '—'}
            href='/equipment'
            caption='Of operational equipment booked today'
            footer={`${d.reservations.equipmentFreeToday} units free today`}
          />
        ) : (
          <PartsView view={partsKind(view, 'stack')} parts={parts} totalLabel='equipment' />
        ))}
    </WidgetQueryState>
  )
}

// ─── CAPA ────────────────────────────────────────────────────────────────────

export function CapaStatusWidget({ time, view }: WidgetBodyProps) {
  const q = useCapaDashboard(time.query)
  const d = q.data
  return (
    <WidgetQueryState isLoading={q.isLoading} error={q.error} isEmpty={false} onRetry={() => void q.refetch()}>
      {d &&
        (view === 'stack' ? (
          // The CAPA page's four figures, as four tiles.
          <div className='grid flex-1 grid-cols-2 gap-2 sm:grid-cols-4'>
            {[
              { label: 'Open', value: d.open, tone: 'text-orange-600', bg: 'bg-orange-50' },
              { label: 'Started', value: d.started, tone: 'text-blue-600', bg: 'bg-blue-50' },
              { label: 'Closed', value: d.closed, tone: 'text-green-600', bg: 'bg-green-50' },
              { label: 'Overdue', value: d.overdue, tone: 'text-red-600', bg: 'bg-red-50' },
            ].map(t => (
              <Link key={t.label} to='/capa' className={cn('rounded-lg p-2.5 transition-shadow hover:shadow-sm', t.bg)}>
                <p className='text-[11px] font-medium text-gray-600'>{t.label}</p>
                <p className={cn('text-2xl font-bold tabular-nums', t.tone)}>{t.value}</p>
              </Link>
            ))}
            <p className='col-span-full text-[11px] text-gray-400'>
              {d.openedInPeriod} raised · {d.closedInPeriod} closed · {windowLabel(time.window)}
            </p>
          </div>
        ) : (
          <PartsView
            view={partsKind(view)}
            totalLabel='CAPAs'
            parts={[
              { label: 'Open, not started', value: Math.max(0, d.open - d.started), color: '#f97316' },
              { label: 'Started', value: d.started, color: STATUS_COLORS.accent },
              { label: 'Closed', value: d.closed, color: STATUS_COLORS.good },
            ]}
          />
        ))}
    </WidgetQueryState>
  )
}

export function OverdueCapaWidget({ time, view }: WidgetBodyProps) {
  const q = useCapaDashboard(time.query)
  const d = q.data
  return (
    <WidgetQueryState isLoading={q.isLoading} error={q.error} isEmpty={false} onRetry={() => void q.refetch()}>
      {d &&
        (view !== 'number' ? (
          <PartsView
            view={partsKind(view, 'donut')}
            totalLabel='open'
            parts={[
              { label: 'Overdue', value: d.overdue, color: STATUS_COLORS.bad },
              { label: 'On track', value: Math.max(0, d.open - d.overdue), color: STATUS_COLORS.good },
            ]}
          />
        ) : (
          <WidgetStat
            value={d.overdue}
            tone={d.overdue > 0 ? 'red' : 'blue'}
            href='/capa'
            caption='Open CAPAs past their target date'
            footer={`of ${d.open} open CAPAs`}
          />
        ))}
    </WidgetQueryState>
  )
}

export function CapaTrendWidget({ time, view }: WidgetBodyProps) {
  const q = useCapaDashboard(time.query)
  const d = q.data
  const rows = (d?.openedByDay ?? []).map((p, i) => ({ day: p.day, opened: p.count, closed: d?.closedByDay[i]?.count ?? 0 }))
  return (
    <WidgetQueryState isLoading={q.isLoading} error={q.error} isEmpty={false} onRetry={() => void q.refetch()}>
      {d && (
        <div className='flex flex-1 flex-col gap-1'>
          <p className='text-xs text-gray-500'>
            <span className='font-semibold text-gray-900'>{d.openedInPeriod}</span> raised ·{' '}
            <span className='font-semibold text-gray-900'>{d.closedInPeriod}</span> closed · {windowLabel(time.window)}
          </p>
          <SeriesView
            view={seriesKind(view, 'lines')}
            rows={rows}
            series={[
              { key: 'opened', label: 'Raised', color: '#f97316' },
              { key: 'closed', label: 'Closed', color: STATUS_COLORS.good },
            ]}
          />
        </div>
      )}
    </WidgetQueryState>
  )
}

const AGE_RAMP = ['#fed7aa', '#fdba74', '#fb923c', '#ea580c']

export function CapaAgeingWidget({ time, view }: WidgetBodyProps) {
  const q = useCapaDashboard(time.query)
  const parts: Part[] = (q.data?.openAge ?? []).map((a, i) => ({ label: a.label, value: a.count, color: AGE_RAMP[i] }))
  return (
    <WidgetQueryState
      isLoading={q.isLoading}
      error={q.error}
      isEmpty={!!q.data && q.data.open === 0}
      emptyText='No CAPAs are open.'
      onRetry={() => void q.refetch()}
    >
      <p className='mb-1 text-[11px] text-gray-400'>Open CAPAs by time since raised</p>
      <PartsView view={partsKind(view)} parts={parts} totalLabel='open' />
    </WidgetQueryState>
  )
}

export function CapaTimeToCloseWidget({ time }: WidgetBodyProps) {
  const q = useCapaDashboard(time.query)
  const prev = useCapaDashboard(previousTimeWindow(time).query)
  const d = q.data
  return (
    <WidgetQueryState isLoading={q.isLoading} error={q.error} isEmpty={false} onRetry={() => void q.refetch()}>
      {d && (
        <WidgetStat
          value={d.meanDaysToClose === null ? '—' : `${d.meanDaysToClose} d`}
          href='/capa'
          caption='Average days from raising a CAPA to closing it'
          delta={
            d.meanDaysToClose !== null &&
            prev.data?.meanDaysToClose != null && (
              <DeltaBadge delta={deltaOf(d.meanDaysToClose, prev.data.meanDaysToClose, false)} period={previousPeriodLabel(time.id)} />
            )
          }
          footer={`${d.closedInPeriod} closed · ${windowLabel(time.window)}`}
        />
      )}
    </WidgetQueryState>
  )
}

// ─── Notifications, audit, integrations ──────────────────────────────────────

const CATEGORY_LABELS: Record<string, string> = {
  PATIENT_INTAKE: 'Patient intake',
  LAB_RESULT: 'Lab result',
  PROCEDURE_DUE: 'Procedure due',
  FORM_NOTIFICATION_RULE: 'Form rule',
}

export function AlertsWidget({ time, view }: WidgetBodyProps) {
  const q = useNotificationsDashboard(time.query)
  const d = q.data
  return (
    <WidgetQueryState
      isLoading={q.isLoading}
      error={q.error}
      isEmpty={view === 'list' && !!d && d.latest.length === 0}
      emptyText='You have no notifications.'
      onRetry={() => void q.refetch()}
    >
      {d &&
        (view === 'number' ? (
          <WidgetStat
            value={d.unread}
            tone={d.unread > 0 ? 'amber' : 'blue'}
            href='/notifications'
            caption='Unread notifications'
            trend={<Sparkline points={d.byDay} label='notifications' />}
            footer={`${d.inPeriod} received · ${windowLabel(time.window)}`}
          />
        ) : (
          <div className='flex flex-1 flex-col gap-1'>
            <p className='text-xs text-gray-500'>
              <span className='font-semibold text-amber-600'>{d.unread} unread</span>
            </p>
            <MiniList>
              {d.latest.map(n => (
                <Row
                  key={n.id}
                  to='/notifications'
                  title={n.message}
                  sub={CATEGORY_LABELS[n.category] ?? humanise(n.category)}
                  aside={shortDate(n.createdAt)}
                  asideTone={n.read ? undefined : 'text-blue-600'}
                />
              ))}
            </MiniList>
          </div>
        ))}
    </WidgetQueryState>
  )
}

export function NotificationsByTypeWidget({ time, view }: WidgetBodyProps) {
  const q = useNotificationsDashboard(time.query)
  const parts = counts(
    (q.data?.byCategory ?? []).map(c => ({ ...c, label: CATEGORY_LABELS[c.label] ?? humanise(c.label) })),
    view === 'bars' ? STATUS_COLORS.accent : undefined,
  )
  return (
    <WidgetQueryState
      isLoading={q.isLoading}
      error={q.error}
      isEmpty={parts.length === 0}
      emptyText='No notifications in this period.'
      onRetry={() => void q.refetch()}
    >
      <PartsView view={partsKind(view)} parts={parts} totalLabel='received' />
    </WidgetQueryState>
  )
}

export function RecentAuditWidget({ time, view }: WidgetBodyProps) {
  const q = useAuditDashboard(time.query)
  const d = q.data
  return (
    <WidgetQueryState
      isLoading={q.isLoading}
      error={q.error}
      isEmpty={view === 'list' && !!d && d.entries === 0}
      emptyText='No audit activity in this period.'
      onRetry={() => void q.refetch()}
    >
      {d &&
        (view === 'number' ? (
          <WidgetStat
            value={d.entries}
            href='/audit'
            caption={`Audit trail entries · ${windowLabel(time.window)}`}
            trend={<Sparkline points={d.byDay} label='entries' />}
            footer={d.byUser[0] ? `Most active: ${d.byUser[0].label} (${d.byUser[0].count})` : undefined}
          />
        ) : (
          <MiniList>
            {d.latest.map(e => (
              <Row
                key={e.id}
                to={`/forms/${encodeURIComponent(e.submissionId)}`}
                title={e.fieldLabel ? `${humanise(e.eventType)}: ${e.fieldLabel}` : humanise(e.eventType)}
                sub={e.changedBy}
                aside={shortDate(e.changedAt)}
              />
            ))}
          </MiniList>
        ))}
    </WidgetQueryState>
  )
}

export function AuditActivityWidget({ time, view }: WidgetBodyProps) {
  const q = useAuditDashboard(time.query)
  const d = q.data
  return (
    <WidgetQueryState isLoading={q.isLoading} error={q.error} isEmpty={false} onRetry={() => void q.refetch()}>
      {d &&
        (view === 'bars' ? (
          <div className='grid flex-1 grid-cols-1 gap-3 md:grid-cols-2'>
            <div>
              <p className='mb-1 text-[11px] text-gray-400'>By kind of change</p>
              <PartsView
                view='bars'
                parts={counts(
                  d.byEvent.map(e => ({ ...e, label: humanise(e.label) })),
                  STATUS_COLORS.accent,
                )}
              />
            </div>
            <div>
              <p className='mb-1 text-[11px] text-gray-400'>By user</p>
              <PartsView view='bars' parts={counts(d.byUser, '#64748b')} />
            </div>
          </div>
        ) : (
          <div className='flex flex-1 flex-col gap-1'>
            <p className='text-xs text-gray-500'>
              <span className='font-semibold text-gray-900'>{d.entries}</span> entries · {windowLabel(time.window)}
            </p>
            <SeriesView
              view={seriesKind(view, 'columns')}
              rows={dailyRows(d.byDay)}
              series={[{ key: 'count', label: 'Entries', color: '#64748b' }]}
            />
          </div>
        ))}
    </WidgetQueryState>
  )
}

export function IntegrationHealthWidget({ time, view }: WidgetBodyProps) {
  const q = useIntegrationsDashboard(time.query)
  const d = q.data
  const rows = (d?.receivedByDay ?? []).map((p, i) => ({ day: p.day, received: p.count, failed: d?.failedByDay[i]?.count ?? 0 }))
  const outcomeColor: Record<string, string> = {
    Processed: STATUS_COLORS.good,
    'No change': '#86efac',
    Waiting: STATUS_COLORS.accentLight,
    Invalid: '#f59e0b',
    Failed: STATUS_COLORS.bad,
  }
  return (
    <WidgetQueryState isLoading={q.isLoading} error={q.error} isEmpty={false} onRetry={() => void q.refetch()}>
      {d &&
        (view === 'number' ? (
          <WidgetStat
            value={d.successPct === null ? '—' : `${d.successPct}%`}
            tone={d.successPct !== null && d.successPct < 95 ? 'amber' : 'blue'}
            href='/integrations'
            caption='Inbound messages handled successfully'
            trend={<Sparkline points={d.failedByDay} label='failed' color={STATUS_COLORS.bad} />}
            footer={`${d.received} received · ${d.failed} failed or invalid`}
          />
        ) : view === 'donut' || view === 'bars' || view === 'table' ? (
          <PartsView
            view={partsKind(view, 'donut')}
            totalLabel='messages'
            parts={d.byOutcome.map(o => ({ label: o.label, value: o.count, color: outcomeColor[o.label] }))}
          />
        ) : (
          <div className='flex flex-1 flex-col gap-1'>
            <p className='text-xs text-gray-500'>
              <span className='font-semibold text-gray-900'>{d.received}</span> received ·{' '}
              <span className='font-semibold text-red-600'>{d.failed}</span> failed
            </p>
            <SeriesView
              view={seriesKind(view, 'lines')}
              rows={rows}
              series={[
                { key: 'received', label: 'Received', color: STATUS_COLORS.accent },
                { key: 'failed', label: 'Failed', color: STATUS_COLORS.bad },
              ]}
            />
          </div>
        ))}
    </WidgetQueryState>
  )
}

export function IntegrationFailuresWidget({ time, view }: WidgetBodyProps) {
  const q = useIntegrationsDashboard(time.query)
  const d = q.data
  const prev = useIntegrationsDashboard(previousTimeWindow(time).query)
  return (
    <WidgetQueryState
      isLoading={q.isLoading}
      error={q.error}
      isEmpty={view === 'list' && !!d && d.failed === 0}
      emptyText='No inbound message failed in this period.'
      onRetry={() => void q.refetch()}
    >
      {d &&
        (view === 'number' ? (
          <WidgetStat
            value={d.failed}
            tone={d.failed > 0 ? 'red' : 'blue'}
            href='/integrations'
            caption='Inbound messages failed or invalid'
            delta={prev.data && <DeltaBadge delta={deltaOf(d.failed, prev.data.failed, false)} period={previousPeriodLabel(time.id)} />}
            footer={`of ${d.received} received · ${windowLabel(time.window)}`}
          />
        ) : (
          <MiniList>
            {d.latestFailures.map(f => (
              <Row key={f.id} to='/integrations' title={f.error ?? 'No error message'} sub={f.type} aside={shortDate(f.receivedAt)} asideTone='text-red-600' />
            ))}
          </MiniList>
        ))}
    </WidgetQueryState>
  )
}
